import { InteractionStatus, InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, MsalProvider, useMsal } from "@azure/msal-react";
import { createContext, FC, PropsWithChildren } from "react";
import { useMe } from "../api/users";
import PageLoader from "../components/ui/PageLoader";
import { Spinner } from "../components/ui/Spinner";
import type { UserResponse } from "../types/user";
import { msalInstance } from "./msalConfig";

export const CurrentUserContext = createContext<UserResponse | null>(null);

function UserProvider({ children }: { children: React.ReactNode }) {
  const { instance } = useMsal();
  const { data: user, isLoading, isError, error } = useMe();
  if (isLoading) return <PageLoader />;
  if (isError) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
      instance.logoutRedirect();
      return <PageLoader />;
    }
  }
  if (!user) return <PageLoader />;
  return <CurrentUserContext.Provider value={user}>{children}</CurrentUserContext.Provider>;
}

const MsalGuard: FC<PropsWithChildren> = ({ children }) => {
  const { inProgress } = useMsal();
  if (inProgress !== InteractionStatus.None) return <Spinner />;
  return <>{children}</>;
};

function AuthGate({ children }: { children: React.ReactNode }) {
  return (
    <MsalGuard>
      <UserProvider>{children}</UserProvider>
    </MsalGuard>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        loadingComponent={PageLoader}
        authenticationRequest={{ redirectUri: window.location.origin }}
      >
        <AuthGate>{children}</AuthGate>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}
